import { useEffect, useState } from 'react';
import { apiClient, useAuth } from '../../auth-lib';
import { useNavigate, useSearchParams } from 'react-router-dom';
import OrgAdminLayout from '../components/OrgAdminLayout';
import { Button, Input, Select, StatusPill, Table, EmptyState, useToast } from '../../design-system';
import ImportClientsModal from '../components/client/ImportClientsModal';
import { CLIENT_SOURCES, IMPORTED_SOURCES, SOURCE_LABELS, shortLabelFor, sourceOf } from '../../lib/clientOrigin';
// TEMPORARY — delete with the purge tool; see the fenced block below.
import PurgeImportedModal from '../components/client/PurgeImportedModal';
import '../styles/OrgAdminClients.css';

const CLIENT_TYPE_LABELS = {
  individual: 'Individual',
  proprietorship: 'Proprietorship',
  partnership: 'Partnership',
  llp: 'LLP',
  private_ltd: 'Private Ltd',
  public_ltd: 'Public Ltd',
  huf: 'HUF',
  trust: 'Trust',
  society: 'Society',
};

function OrgAdminClients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('all');
  const [clientType, setClientType] = useState('all');
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState(() => searchParams.get('q') || '');
  const [importOpen, setImportOpen] = useState(false);
  // Where each client came from: typed in, a spreadsheet, or one of the two
  // Tally imports. 'all' is the resting state — provenance is a thing you go
  // looking for, not a lens the list should default to.
  const [source, setSource] = useState('all');
  const [purgeOpen, setPurgeOpen] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  // The detail page is gated by `clients.view-profile`; without it the name is
  // plain text (not a link) so the row can't bounce the user off a 403 page.
  const canViewProfile = user?.isSuperAdmin || user?.permissions?.includes('clients.view-profile');
  // TEMPORARY — the purge tool's visibility. Admin (5) or a super admin, and
  // only while the list is narrowed to a source that can actually be purged.
  const canPurge =
    (user?.isSuperAdmin || user?.role?.level >= 5) && IMPORTED_SOURCES.includes(source);

  // Debounce the free-text search so each keystroke isn't its own request —
  // the backend now does the name filtering (regex on displayName) server-side.
  useEffect(() => {
    const handle = setTimeout(fetchClients, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, clientType, search, source]);

  const fetchClients = async () => {
    try {
      setLoading(true);
      const params = {};
      if (status !== 'all') params.status = status;
      if (clientType !== 'all') params.clientType = clientType;
      if (search.trim()) params.q = search.trim();
      if (source !== 'all') params.source = source;
      const response = await apiClient.get('/api/clients', { params });
      setClients(response.data);
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const filtered = clients;

  const columns = [
    { key: 'clientCode', header: 'Code', render: (c) => c.clientCode || '-' },
    {
      key: 'displayName',
      header: 'Client',
      render: (c) =>
        canViewProfile ? (
          <button className="client-name-link" onClick={() => navigate(`/org-admin/clients/${c._id}`)}>
            {c.displayName}
          </button>
        ) : (
          <span>{c.displayName}</span>
        ),
    },
    { key: 'clientType', header: 'Type', render: (c) => CLIENT_TYPE_LABELS[c.clientType] || c.clientType },
    { key: 'pan', header: 'PAN', render: (c) => c.pan || '-' },
    { key: 'partner', header: 'Partner', render: (c) => c.assignedPartnerId?.name || '-' },
    { key: 'manager', header: 'Manager', render: (c) => c.assignedManagerId?.name || '-' },
    {
      key: 'source',
      header: 'Source',
      // How the record got here. A client the firm typed was checked by a
      // person; one off a Tally company scan is a name and a placeholder
      // entity type waiting to be finished — worth telling apart at a glance.
      render: (c) => <span className={`client-source client-source-${sourceOf(c)}`}>{shortLabelFor(sourceOf(c))}</span>,
    },
    { key: 'status', header: 'Status', render: (c) => <StatusPill status={c.status} /> },
  ];

  return (
    <OrgAdminLayout title="Clients" subtitle="Manage client records and compliance">
      <div className="clients-page">
        <div className="clients-toolbar">
          <Input
            placeholder="Search by name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="search-input"
          />
          <Select value={status} onChange={(e) => setStatus(e.target.value)} className="status-filter">
            <option value="all">All Statuses</option>
            <option value="onboarding">Onboarding</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="closed">Closed</option>
          </Select>
          <Select value={clientType} onChange={(e) => setClientType(e.target.value)} className="status-filter">
            <option value="all">All Types</option>
            {Object.entries(CLIENT_TYPE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </Select>
          <Select value={source} onChange={(e) => setSource(e.target.value)} className="status-filter">
            <option value="all">All Sources</option>
            {CLIENT_SOURCES.map((s) => (
              <option key={s} value={s}>{SOURCE_LABELS[s]}</option>
            ))}
          </Select>
          {/* ══ TEMPORARY — testing tool. Delete this element, the modal
              below, the two imports at the top and `purgeOpen` when the Tally
              connector work is done. ═══════════════════════════════════════
              ⚠️ It appears ONLY when the list is already narrowed to a
              purgeable source, so the button can never act on a wider set
              than the rows on screen — which is the whole basis on which
              somebody presses it. Admin-only here AND on the server; this
              check only keeps a control nobody may use off the screen. */}
          {canPurge && (
            <Button variant="danger" onClick={() => setPurgeOpen(true)}>
              🗑 Delete these imports
            </Button>
          )}
          <Button variant="secondary" onClick={() => setImportOpen(true)}>
            ⇧ Import
          </Button>
          <Button variant="primary" onClick={() => navigate('/org-admin/clients/create')}>
            + Add Client
          </Button>
        </div>

        {!loading && filtered.length === 0 ? (
          <EmptyState
            icon="🤝"
            title="No clients found"
            description="Add your first client to start tracking their compliance calendar."
            action={
              <Button variant="secondary" onClick={() => navigate('/org-admin/clients/create')}>
                Create your first client
              </Button>
            }
          />
        ) : (
          <Table columns={columns} data={filtered} loading={loading} />
        )}

        <ImportClientsModal
          isOpen={importOpen}
          onClose={() => setImportOpen(false)}
          onImported={fetchClients}
        />

        {/* TEMPORARY — delete with the rest of the purge tool. */}
        {canPurge && (
          <PurgeImportedModal
            isOpen={purgeOpen}
            onClose={() => setPurgeOpen(false)}
            source={source}
            onPurged={fetchClients}
          />
        )}
      </div>
    </OrgAdminLayout>
  );
}

export default OrgAdminClients;
